import { css, cx } from '@repo/pandacss/styled-system/css';
import { AnalyticsSkuViewed } from '@repo/segment/Analytics/AnalyticsSkuViewed';
import { SEGMENT_SKU_VIEWED_SOURCE } from '@repo/segment/Analytics/constants';
import { MarketplaceEntityTypeEnum } from '@repo/supergraph/marketplace/enums';
import type { ProductCard as ProductCardType } from '@repo/supergraph/roundup/base/types';
import type { MobileOrDesktop } from '@repo/utils/deviceType/types';
import React from 'react';
import { ProductCard } from '../ProductCard';
import { SECTION_NAME } from '../ProductCard/ImageCtaReviewSection/constants';

interface Props {
  cardBadgeText?: string;
  card: ProductCardType;
  cardIndex: number;
  deviceType?: MobileOrDesktop;
  productPositionBase: number;
  isOurTakeExpanded: boolean;
  pageType?: 'roundup' | 'paid-roundup';
  shouldApplyStylingUpdates?: boolean;
  slug?: string;
}

export default function ProductCardWrapper({
  cardBadgeText,
  card,
  cardIndex,
  deviceType,
  productPositionBase,
  isOurTakeExpanded,
  pageType,
  shouldApplyStylingUpdates = false,
  slug,
}: Props): React.JSX.Element {
  const entity = card.marketplaceEntity;
  const elementPosition = cardIndex + productPositionBase;

  const overrideCta = entity.ctaOverride || entity.cta;

  const { drivers, entityType, institution, name, offerForCta, product, productType } = entity;
  const monetizable = offerForCta?.monetizable ?? entity.monetizable!;
  const offerId = offerForCta?.id ? offerForCta.id : entity.id;
  const offerName = offerForCta?.name ? offerForCta.name : name;
  const ctaAnalytics = {
    index: elementPosition,
    ...(institution?.id && { institutionId: institution.id }),
    ...(entityType === MarketplaceEntityTypeEnum.Institutions && { institutionId: entity.id }),
    offerId,
    ...(product?.id && { productId: product.id }),
    productType,
    monetizable,
  };

  return (
    <li
      className={cx(
        css({ listStyleType: 'none' }),
        cardIndex !== 0 && css({ marginTop: '11' }),
      )}
      key={`product-card-${elementPosition}`}
    >
      <AnalyticsSkuViewed
        source={SEGMENT_SKU_VIEWED_SOURCE}
        name={offerName}
        productType={productType}
        level="offer"
        institutionId={
          entityType === MarketplaceEntityTypeEnum.Institutions ? entity.id : entity.institution?.id
        }
        institutionName={
          entityType === MarketplaceEntityTypeEnum.Institutions ? name : entity.institution?.name
        }
        productId={entity.product?.id}
        productName={entity.product?.name}
        offerId={offerId}
        offerName={offerName}
        monetizable={monetizable}
        impressionSection={SECTION_NAME}
        impressionPosition={elementPosition}
        additionalAttributes={{
          page_type: 'roundup',
        }}
      >
        <div>
          <ProductCard
            ctaAnalytics={ctaAnalytics}
            bottomLine={card.bottomLine}
            cta={overrideCta}
            deviceType={deviceType}
            displayName={entity.displayName}
            drivers={drivers}
            federallyInsuredAnnotation={entity.federallyInsuredAnnotation}
            image={entity.image}
            monetizable={monetizable}
            pageType={pageType}
            starRating={entity.starRating}
            starRatingTooltip={entity.starRatingTooltip}
            reviewLink={entity.reviewLink}
            isOurTakeExpanded={isOurTakeExpanded}
            productType={entity.productType}
            subProductType={entity.subProductType}
            pros={card.pros}
            cons={card.cons}
            spotlightHeading={card.spotlightHeading}
            spotlightDetails={card.spotlightDetails}
            authorQuote={card.authorQuote}
            oneYearApyEntity={card.oneYearApyEntity}
            threeYearApyEntity={card.threeYearApyEntity}
            fiveYearApyEntity={card.fiveYearApyEntity}
            cardBadgeText={cardBadgeText}
            shouldApplyStylingUpdates={shouldApplyStylingUpdates}
            slug={slug ?? ''}
            hideBestOfAwardsBanner={card.hideBestOfAwardsBanner}
          />
        </div>
      </AnalyticsSkuViewed>
    </li>
  );
}
